import * as React from "react";
import { ArrowRight, X, ShieldCheck } from "lucide-react";
import { SiTiktok } from "react-icons/si";
import { FaInstagram, FaYoutube } from "react-icons/fa";
import { useTranslation } from "react-i18next";

import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemSeparator,
  ItemTitle,
} from "@/components/ui/item";
import { GlassPanel } from "../ui/GlassPanel";
import { Button } from "@/components/ui/button";
import {
  InstagramSection,
  YouTubeSection,
  TikTokSection,
  useInstagram,
} from "@/features/account";
import { useYouTubeConnections } from "@/features/account/hooks/useYouTubeConnections";
import { useTikTokConnections } from "@/features/account/hooks/useTikTokConnections";
import { useShowConnectAccount } from "@/hooks/useShowConnectAccount";

type Provider = "instagram" | "youtube" | "tiktok";

const SOCIAL_ACCOUNTS: Array<{
  icon: React.ReactNode;
  provider: Provider;
}> = [
  {
    icon: <FaInstagram className="mx-auto h-14 w-14 md:h-16 md:w-16" />,
    provider: "instagram",
  },
  {
    icon: <FaYoutube className="mx-auto h-14 w-14 md:h-16 md:w-16" />,
    provider: "youtube",
  },
  {
    icon: <SiTiktok className="mx-auto h-14 w-14 md:h-16 md:w-16" />,
    provider: "tiktok",
  },
];

export function ConnectAccountPopUp({ onClose }: { onClose?: () => void }) {
  const { t } = useTranslation();
  const { shouldShow, providerCounts, isLoading } = useShowConnectAccount();
  const [activeProvider, setActiveProvider] = React.useState<Provider | null>(
    null
  );

  const handleSelectProvider = React.useCallback((provider: Provider) => {
    setActiveProvider(provider);
  }, []);

  const handleReset = React.useCallback(() => {
    setActiveProvider(null);
  }, []);

  if (isLoading || !shouldShow) {
    return null;
  }

  return (
    <GlassPanel
      variant="solid"
      className="flex w-full max-w-xl flex-col gap-6 p-2 relative"
    >
      {onClose && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="absolute top-2 right-2 text-neutral-400 hover:text-black  hover:bg-white z-10"
          aria-label={t("common.close", "Close")}
        >
          <X size={20} />
        </Button>
      )}
      {activeProvider ? (
        <ProviderFlow
          provider={activeProvider}
          onBack={handleReset}
          onConnectMore={handleReset}
        />
      ) : (
        <>
          <div className="text-center space-y-2 pt-6">
            <h3 className="text-xl font-semibold text-white">
              {t("connectPopup.title", "Connect an account")}
            </h3>
            <p className="text-sm text-neutral-300">
              {t(
                "connectPopup.subtitle",
                "Lema works better personalized"
              )}
            </p>
          </div>
          <ItemGroup>
            {SOCIAL_ACCOUNTS.map((account, index) => (
              <React.Fragment key={account.provider}>
                <Item>
                  <ItemMedia>{account.icon}</ItemMedia>
                  <ItemContent className="gap-1">
                    <ItemTitle>
                      {t(
                        `connectPopup.providers.${account.provider}.name`,
                        account.provider === "instagram"
                          ? "Instagram"
                          : account.provider === "youtube"
                          ? "YouTube"
                          : "TikTok"
                      )}
                    </ItemTitle>
                    <ItemDescription>
                      {t(
                        `connectPopup.providers.${account.provider}.description`,
                        account.provider === "instagram"
                          ? "Connect your Instagram account."
                          : account.provider === "youtube"
                          ? "Connect your YouTube account."
                          : "Connect your TikTok account."
                      )}
                      {providerCounts[account.provider] > 0 && (
                        <span className="ml-2 text-xs text-neutral-400">
                          {t("connectPopup.active_accounts", {
                            count: providerCounts[account.provider],
                            defaultValue: `(${providerCounts[account.provider]} active)`,
                          })}
                        </span>
                      )}
                    </ItemDescription>
                  </ItemContent>
                  <ItemActions>
                    <Button
                      onClick={() => handleSelectProvider(account.provider)}
                      variant="highlight"
                      className="inline-flex items-center gap-2 text-sm text-black transition-colors"
                    >
                      <span className="inline-flex items-center gap-1">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        <span>{t("connectPopup.connect_button", "Secure Connect")}</span>
                      </span>
                      <ArrowRight className="h-4 w-4 text-black" />
                    </Button>
                  </ItemActions>
                </Item>
                {index !== SOCIAL_ACCOUNTS.length - 1 && <ItemSeparator />}
              </React.Fragment>
            ))}
          </ItemGroup>
        </>
      )}
    </GlassPanel>
  );
}

type ProviderFlowProps = {
  provider: Provider;
  onBack: () => void;
  onConnectMore: () => void;
};

function ProviderFlow({ provider, onBack, onConnectMore }: ProviderFlowProps) {
  // Always redirect to /account after OAuth so that useTikTokConnections /
  // useYouTubeConnections (mounted on that page) can detect the success param
  // and trigger auto-indexing.  If the redirect stayed on the current chat
  // page the hooks would not be mounted after the fresh component remount,
  // and auto-index would silently never fire.
  const redirectTo = "/account";

  switch (provider) {
    case "instagram":
      return <InstagramFlow onBack={onBack} onConnectMore={onConnectMore} />;
    case "youtube":
      return (
        <YouTubeFlow onBack={onBack} onConnectMore={onConnectMore} redirectTo={redirectTo} />
      );
    case "tiktok":
      return (
        <TikTokFlow onBack={onBack} onConnectMore={onConnectMore} redirectTo={redirectTo} />
      );
    default:
      return null;
  }
}

type FlowProps = {
  onBack: () => void;
  onConnectMore: () => void;
  redirectTo?: string;
};

function InstagramFlow({ onBack, onConnectMore }: FlowProps) {
  const instagram = useInstagram();
  const isConnected = instagram.connection.currentConnectionType !== null;

  return (
    <InstagramSection
      isLoadingInstagram={instagram.isLoadingInstagram}
      isConnecting={instagram.isConnecting}
      connection={instagram.connection}
      isIndexing={instagram.isIndexing}
      currentMessageIndex={instagram.currentMessageIndex}
      indexingMessages={instagram.indexingMessages}
      onConnectViaFacebook={instagram.connectInstagramViaFacebook}
      onConnectDirect={instagram.connectInstagramDirect}
      onDisconnect={instagram.disconnectInstagram}
      onTriggerIndex={instagram.triggerIndex}
      instagramSelectionState={instagram.instagramSelectionState}
      onFetchAvailableAccounts={instagram.fetchAvailableInstagramAccounts}
      onSelectAccount={instagram.selectInstagramAccount}
      onReopenSelection={instagram.reopenInstagramSelection}
      onCloseSelection={instagram.closeInstagramSelection}
      variant="popup"
      onBack={onBack}
      onConnectMore={isConnected ? onConnectMore : undefined}
    />
  );
}

function YouTubeFlow({ onBack, onConnectMore, redirectTo }: FlowProps) {
  const youtubeHook = useYouTubeConnections();
  const hasAccounts = youtubeHook.accounts.length > 0;

  return (
    <YouTubeSection
      hook={youtubeHook}
      variant="popup"
      onBack={onBack}
      onConnectMore={hasAccounts ? onConnectMore : undefined}
      redirectTo={redirectTo}
    />
  );
}

function TikTokFlow({ onBack, onConnectMore, redirectTo }: FlowProps) {
  const tikTokHook = useTikTokConnections();
  const hasAccounts = tikTokHook.accounts.length > 0;

  return (
    <TikTokSection
      hook={tikTokHook}
      variant="popup"
      onBack={onBack}
      onConnectMore={hasAccounts ? onConnectMore : undefined}
      redirectTo={redirectTo}
    />
  );
}
 
